import { batch, computed, effect, signal } from '@preact/signals'
import { api } from '../services/api'
import {
  initKeycloak,
  login as kcLogin,
  logout as kcLogout,
  register as kcRegister,
} from '../services/keycloak'

// ========================================
// Dev Mode - Skip Auth
// ========================================

/**
 * When VITE_DEV_SKIP_AUTH=true, bypass Keycloak entirely and load the game directly.
 * Useful for rapid game development iteration without auth flow.
 */
export const DEV_SKIP_AUTH = import.meta.env.DEV && import.meta.env.VITE_DEV_SKIP_AUTH === 'true'

// ========================================
// Types
// ========================================

export interface Identity {
  id: string
  keycloakSub: string
  email: string | null
  createdAt: string
}

export interface Profile {
  id: string
  type: 'CHILD' | 'ADULT'
  displayName: string
  childLinkState: 'UNLINKED' | 'PENDING' | 'LINKED' | null
  createdAt: string
}

export interface ManagedChild {
  id: string
  displayName: string
  childLinkState: string | null
  accessRole: string
  guardianKind: string
}

export interface GuardianLinkResolveResult {
  valid: boolean
  childDisplayName?: string
  suggestedKind?: string
  suggestedRole?: string
  error?: string
}

export interface GatedActionRequest {
  id: string
  childId: string
  childDisplayName: string
  requestType: string
  payload: Record<string, unknown>
  status: 'PENDING' | 'APPROVED' | 'DENIED' | 'EXPIRED'
  createdAt: string
  expiresAt: string | null
  approvalCount: number
}

// ========================================
// Signals
// ========================================

// Core auth state
export const isInitialized = signal(false)
export const isLoading = signal(true)
export const isAuthenticated = signal(false)

// User data
export const identity = signal<Identity | null>(null)
export const profile = signal<Profile | null>(null)
export const managedChildren = signal<ManagedChild[]>([])
export const guardianRequests = signal<GatedActionRequest[]>([])

// Error state
export const authError = signal<string | null>(null)

// ========================================
// Computed Values
// ========================================

export const isChild = computed(() => profile.value?.type === 'CHILD')
export const isAdult = computed(() => profile.value?.type === 'ADULT')
export const isLinked = computed(() => profile.value?.childLinkState === 'LINKED')
export const needsGuardian = computed(
  () => isChild.value && profile.value?.childLinkState !== 'LINKED',
)
export const hasProfile = computed(() => profile.value !== null)

// ========================================
// Actions
// ========================================

/**
 * Initialize auth system
 * Call once at app startup
 */
export async function initAuth(): Promise<void> {
  // Dev mode: skip Keycloak entirely
  if (DEV_SKIP_AUTH) {
    console.log('[Auth] DEV_SKIP_AUTH enabled - bypassing Keycloak')
    batch(() => {
      isAuthenticated.value = true
      identity.value = {
        id: 'dev-identity',
        keycloakSub: 'dev-sub',
        email: 'dev@localhost',
        createdAt: new Date().toISOString(),
      }
      profile.value = {
        id: 'dev-profile',
        type: 'CHILD',
        displayName: 'Dev Player',
        childLinkState: 'LINKED',
        createdAt: new Date().toISOString(),
      }
      isInitialized.value = true
      isLoading.value = false
    })
    return
  }

  try {
    isLoading.value = true
    authError.value = null

    // Initialize Keycloak
    const keycloak = await initKeycloak()
    isAuthenticated.value = keycloak.authenticated || false

    // If authenticated, fetch user data
    if (keycloak.authenticated) {
      await fetchCurrentUser()
    }

    isInitialized.value = true
  } catch (error) {
    console.error('[Auth] Initialization failed:', error)
    authError.value = 'Failed to initialize authentication'
    isInitialized.value = true // Still mark as initialized to show UI
  } finally {
    isLoading.value = false
  }
}

/**
 * Fetch current user data from API
 */
export async function fetchCurrentUser(): Promise<void> {
  try {
    isLoading.value = true
    authError.value = null

    const response = await api.get<{
      identity: Identity
      profile: Profile | null
      managedChildren: ManagedChild[]
    }>('/auth/me')

    batch(() => {
      identity.value = response.identity
      profile.value = response.profile
      managedChildren.value = response.managedChildren
    })
  } catch (error) {
    console.error('[Auth] Failed to fetch user:', error)
    authError.value = 'Failed to load user data'
    throw error
  } finally {
    isLoading.value = false
  }
}

/**
 * Sign up with invite code
 */
export async function signup(inviteCode: string, displayName: string): Promise<Profile> {
  try {
    isLoading.value = true
    authError.value = null

    const response = await api.post<{
      identity: Identity
      profile: Profile
    }>('/auth/signup', {
      inviteCode,
      displayName,
    })

    batch(() => {
      identity.value = response.identity
      profile.value = response.profile
    })

    return response.profile
  } catch (error: unknown) {
    const message = (error as { message?: string })?.message || 'Signup failed'
    authError.value = message
    throw new Error(message)
  } finally {
    isLoading.value = false
  }
}

/**
 * Redirect to Keycloak login
 */
export function login(redirectUri?: string): void {
  kcLogin(redirectUri)
}

/**
 * Redirect to Keycloak registration (for new users with invite codes)
 * @param inviteCode - The validated invite code (passed through Keycloak flow)
 * @param inviteKind - The invite kind (CHILD or ADULT)
 */
export function register(inviteCode?: string, inviteKind?: string): void {
  kcRegister(inviteCode, inviteKind)
}

/**
 * Redirect to Keycloak logout
 */
export function logout(): void {
  batch(() => {
    identity.value = null
    profile.value = null
    managedChildren.value = []
    isAuthenticated.value = false
  })
  kcLogout()
}

/**
 * Request guardian link (child action)
 */
export async function requestGuardianLink(guardianEmail: string): Promise<{ requestId: string }> {
  if (!profile.value || profile.value.type !== 'CHILD') {
    throw new Error('Only children can request guardian links')
  }

  try {
    isLoading.value = true
    authError.value = null

    const response = await api.post<{ success: boolean; requestId: string }>(
      `/children/${profile.value.id}/guardian-link-requests`,
      { guardianEmail },
    )

    // Update local state
    if (profile.value) {
      profile.value = { ...profile.value, childLinkState: 'PENDING' }
    }

    return { requestId: response.requestId }
  } catch (error: unknown) {
    const message = (error as { message?: string })?.message || 'Failed to request guardian link'
    authError.value = message
    throw error
  } finally {
    isLoading.value = false
  }
}

/**
 * Clear error state
 */
export function clearAuthError(): void {
  authError.value = null
}

/**
 * Resolve guardian link token (public endpoint - no auth required)
 * Used to validate token and get child info before approve/deny
 */
export async function resolveGuardianLinkToken(token: string): Promise<GuardianLinkResolveResult> {
  try {
    isLoading.value = true
    authError.value = null

    const response = await api.get<GuardianLinkResolveResult>(
      `/guardian-link/resolve?token=${encodeURIComponent(token)}`,
    )

    return response
  } catch (error: unknown) {
    const message = (error as { message?: string })?.message || 'Failed to validate guardian link'
    authError.value = message
    return { valid: false, error: message }
  } finally {
    isLoading.value = false
  }
}

/**
 * Approve guardian link (creates delegated access, and adult profile if needed)
 */
export async function approveGuardianLink(
  token: string,
  kind: string,
  role: string,
): Promise<{ success: boolean; delegatedAccessId?: string }> {
  try {
    isLoading.value = true
    authError.value = null

    const response = await api.post<{ success: boolean; delegatedAccessId: string }>(
      '/guardian-link/approve',
      { token, kind, role },
    )

    // Refresh user data to get updated profile and managed children
    await fetchCurrentUser()

    return response
  } catch (error: unknown) {
    const message = (error as { message?: string })?.message || 'Failed to approve guardian link'
    authError.value = message
    throw new Error(message)
  } finally {
    isLoading.value = false
  }
}

/**
 * Deny guardian link
 */
export async function denyGuardianLink(token: string): Promise<{ success: boolean }> {
  try {
    isLoading.value = true
    authError.value = null

    const response = await api.post<{ success: boolean }>('/guardian-link/deny', { token })

    return response
  } catch (error: unknown) {
    const message = (error as { message?: string })?.message || 'Failed to deny guardian link'
    authError.value = message
    throw new Error(message)
  } finally {
    isLoading.value = false
  }
}

/**
 * Fetch all pending gated action requests for guardian
 */
export async function fetchGuardianRequests(): Promise<GatedActionRequest[]> {
  try {
    isLoading.value = true
    authError.value = null

    const response = await api.get<{ requests: GatedActionRequest[] }>('/guardian/requests')
    guardianRequests.value = response.requests

    return response.requests
  } catch (error: unknown) {
    const message = (error as { message?: string })?.message || 'Failed to fetch requests'
    authError.value = message
    throw new Error(message)
  } finally {
    isLoading.value = false
  }
}

/**
 * Approve a gated action request
 */
export async function approveRequest(
  requestId: string,
): Promise<{ fullyApproved: boolean; message: string }> {
  try {
    isLoading.value = true
    authError.value = null

    const response = await api.post<{ success: boolean; fullyApproved: boolean; message: string }>(
      `/requests/${requestId}/approve`,
    )

    // Refresh requests list
    await fetchGuardianRequests()

    return { fullyApproved: response.fullyApproved, message: response.message }
  } catch (error: unknown) {
    const message = (error as { message?: string })?.message || 'Failed to approve request'
    authError.value = message
    throw new Error(message)
  } finally {
    isLoading.value = false
  }
}

/**
 * Deny a gated action request
 */
export async function denyRequest(
  requestId: string,
): Promise<{ success: boolean; message: string }> {
  try {
    isLoading.value = true
    authError.value = null

    const response = await api.post<{ success: boolean; message: string }>(
      `/requests/${requestId}/deny`,
    )

    // Refresh requests list
    await fetchGuardianRequests()

    return response
  } catch (error: unknown) {
    const message = (error as { message?: string })?.message || 'Failed to deny request'
    authError.value = message
    throw new Error(message)
  } finally {
    isLoading.value = false
  }
}

// ========================================
// Effects
// ========================================

// Log auth state changes (dev only)
if (import.meta.env.DEV) {
  effect(() => {
    console.debug('[Auth] State:', {
      isInitialized: isInitialized.value,
      isAuthenticated: isAuthenticated.value,
      profile: profile.value?.displayName,
      childLinkState: profile.value?.childLinkState,
    })
  })
}
